
import React from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const GameFAQ = () => {
  const faqs = [
    {
      question: "What is Pokemon Clicker?",
      answer: "Pokemon Clicker is a free online incremental game based on the Pokemon universe. Players catch, train, and evolve Pokemon through clicking mechanics, building a collection while progressing through different regions and challenges. It combines elements of the classic Pokemon games with idle gameplay that continues even when you're not actively playing."
    },
    {
      question: "How do I play Pokemon Clicker?",
      answer: "The basic gameplay involves clicking to catch Pokemon and earn resources. As you progress, you'll unlock auto-clickers and other features that generate resources automatically. You can use these resources to upgrade your Pokemon, purchase items, and unlock new regions. The game also includes features like gym battles, evolution mechanics, and special events to keep gameplay varied and engaging."
    },
    {
      question: "Do I need to download anything to play Pokemon Clicker?",
      answer: "No, Pokemon Clicker is a browser-based game that runs entirely online. There's no need to download or install any software. Simply visit the game page and start playing immediately on any device with a web browser and internet connection. This makes it accessible across desktop computers, laptops, tablets, and mobile phones."
    },
    {
      question: "Is Pokemon Clicker free to play?",
      answer: "Yes, Pokemon Clicker is completely free to play. You can enjoy all core gameplay features without spending any money. The game is supported by advertisements, but these don't interfere with the main gameplay experience. While some incremental games offer premium currencies or paid advantages, Pokemon Clicker focuses on providing a fair and enjoyable experience for all players regardless of spending."
    },
    {
      question: "Which Pokemon can I catch in Pokemon Clicker?",
      answer: "Pokemon Clicker features hundreds of Pokemon from across multiple generations of the franchise. You'll start with common Pokemon like Pidgey and Rattata, but as you progress through different regions, you'll have the opportunity to catch rarer species, including starter Pokemon, fossil Pokemon, and even legendary Pokemon. The game regularly updates to add new Pokemon from the latest generations."
    },
    {
      question: "How does evolution work in Pokemon Clicker?",
      answer: "Evolution in Pokemon Clicker works similarly to the main Pokemon games, but with some adjustments for the clicker format. You'll need to collect Pokemon Candy specific to each Pokemon species. Once you have enough candy, you can evolve your Pokemon to its next form, increasing its stats and abilities. Some Pokemon also require special Evolution Stones or other conditions to evolve, adding strategic depth to the collection process."
    },
    {
      question: "What are the different regions in Pokemon Clicker?",
      answer: "Pokemon Clicker includes most of the iconic regions from the Pokemon franchise, including Kanto, Johto, Hoenn, Sinnoh, Unova, Kalos, Alola, and Galar. Each region has its own set of Pokemon to catch, gym leaders to challenge, and exclusive items to find. You'll need to earn gym badges and meet certain requirements to unlock new regions as you progress through the game."
    },
    {
      question: "How do gym battles work in Pokemon Clicker?",
      answer: "Gym battles in Pokemon Clicker are simplified versions of the battles in the main Pokemon games. Your team's overall power level, type advantages, and special abilities determine your chances of victory. When you challenge a gym, you'll see an animated battle sequence followed by the results. Winning gym battles earns you badges, which unlock new areas and provide permanent bonuses to your clicking power and resource generation rates."
    },
    {
      question: "Does my progress save automatically in Pokemon Clicker?",
      answer: "Yes, Pokemon Clicker automatically saves your progress to your browser's local storage. This means you can close the game and return later without losing any progress. However, it's important to note that clearing your browser data or cookies will erase this saved data. Some versions of the game also offer cloud save options or account systems for more secure progress tracking across multiple devices."
    },
    {
      question: "Are there special events in Pokemon Clicker?",
      answer: "Yes, Pokemon Clicker features regular special events that offer unique rewards and challenges. These events might be themed around holidays, Pokemon anniversaries, or new Pokemon game releases. During events, you can often catch limited-time Pokemon, earn rare items, or benefit from boosted resource generation. Keeping an eye on event schedules and participating actively can significantly accelerate your progress in the game."
    },
    {
      question: "How can I increase my clicking efficiency in Pokemon Clicker?",
      answer: "To maximize your clicking efficiency, focus on purchasing upgrades that increase your click value and investing in auto-clickers that generate clicks automatically. Assembling a team of high-level Pokemon with complementary abilities also boosts your clicking power. Additionally, completing achievements, earning gym badges, and participating in special events can provide permanent multipliers to your clicking efficiency. For advanced players, timing your prestiges optimally can lead to exponential growth in clicking power."
    },
    {
      question: "Can I play Pokemon Clicker on mobile devices?",
      answer: "Yes, Pokemon Clicker is designed with responsive controls that work well on both desktop and mobile devices. The touch interface adapts to smaller screens, making it easy to tap instead of click for core gameplay actions. However, some features may be more comfortably accessed on larger screens. The game performs best on devices with updated browsers and reasonable processing power, as some of the animations and calculations can be resource-intensive."
    }
  ];

  return (
    <section className="my-16" id="faq">
      <h2 className="text-3xl font-bold mb-8 text-center text-yellow-300 drop-shadow-lg">Frequently Asked Questions</h2>
      
      <div className="bg-blue-800/50 border border-blue-600 rounded-lg shadow-lg p-6">
        <Accordion type="single" collapsible className="space-y-4">
          {faqs.map((faq, index) => (
            <AccordionItem key={index} value={`item-${index}`} className="border-blue-600">
              <AccordionTrigger className="text-lg font-medium text-yellow-200 hover:text-yellow-100">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="text-blue-100 leading-relaxed">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        <div className="mt-8 pt-6 border-t border-blue-700">
          <h3 className="text-xl font-semibold mb-4 text-yellow-200">Still Have Questions?</h3>
          <p className="text-blue-100 mb-4">
            The Pokemon Clicker community is active and helpful. If you have questions not covered in this FAQ, consider checking out these resources:
          </p>
          <ul className="list-disc pl-6 text-blue-100 space-y-2">
            <li>The official Pokemon Clicker community forum</li>
            <li>The game's documentation and wiki</li>
            <li>Social media groups dedicated to Pokemon Clicker</li>
            <li>The in-game help section, which includes tutorials and guides</li>
          </ul>
        </div>
      </div>

      <div className="mt-12 text-center">
        <h3 className="text-2xl font-bold text-yellow-300 mb-4">Ready to Start Your Pokemon Clicker Adventure?</h3>
        <p className="text-blue-100 max-w-2xl mx-auto mb-6">
          Jump into the world of Pokemon Clicker and begin your journey to become a clicking master. Catch rare Pokemon, train powerful teams, and challenge gym leaders across multiple regions!
        </p>
        <a 
          href="#play-now" 
          className="inline-block bg-gradient-to-r from-red-500 to-yellow-500 hover:from-red-600 hover:to-yellow-600 text-white font-bold py-3 px-8 rounded-full transition-all transform hover:scale-105 hover:shadow-lg"
        >
          Play Pokemon Clicker Now
        </a>
      </div>
    </section>
  );
};

export default GameFAQ;
